import { TIME_GROUPS } from '../../inputPatterns/time/constants';
import { TimeFormatType } from '../../inputPatterns/time/formats';
import { TimeParts } from '../../types';
import { isStrictMatch } from '../../utils/internal/isStrictMatch';
import { parse } from '../../utils/internal/parseUnitInRange';
import { getHours, getMatchedTimeFormat } from './helpers/timeHelpers';

type ExtractTimeComponentsArgs = {
  value: string;
  supportedFormats: TimeFormatType[];
  isStrictMode?: boolean;
};
/**
 * Extracts time components (hours and seconds) based on recognized time formats.
 *
 * @param {string} value - The date string to extract from.
 * @returns {{ hours: number, seconds: number } | undefined} Extracted time parts or `undefined` if invalid.
 */

export const extractTimeComponents = ({
  value,
  supportedFormats,
  isStrictMode = true,
}: ExtractTimeComponentsArgs): TimeParts => {
  const timeMatch = getMatchedTimeFormat(value, supportedFormats);
  if (!timeMatch || (isStrictMode && !isStrictMatch(value, timeMatch.match))) return {}; // No match, invalid format or is not a strict match

  const { matchedGroups } = timeMatch;
  if (!matchedGroups) return {}; // No match, invalid format
  const {
    [TIME_GROUPS.HOUR_12]: hour12,
    [TIME_GROUPS.HOUR_24]: hour24,
    [TIME_GROUPS.MERIDIEM]: meridiem,
    [TIME_GROUPS.MINUTE]: minutes,
    [TIME_GROUPS.SECOND]: seconds,
    [TIME_GROUPS.MILLISECOND]: milliseconds,
  } = matchedGroups;

  // Only include the field if it was matched (exists as input)
  const result: Record<string, number | undefined> = {};

  if (hour12 || hour24) {
    result.hours = getHours({ hour12, hour24, meridiem }) ?? -1;
  }

  if (minutes !== undefined) {
    result.minutes = parse(minutes, 0, 59);
  }

  if (seconds !== undefined) {
    result.seconds = parse(seconds, 0, 59);
  }

  if (milliseconds !== undefined) {
    result.milliseconds = parse(milliseconds, 0, 999);
  }

  return result;
};
